import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Builds a small random maths question, e.g. "7 + 4" or "9 - 3"
const generateQuestion = () => {
  const a = Math.floor(Math.random() * 9) + 1; // 1-9
  const b = Math.floor(Math.random() * 9) + 1; // 1-9
  const useMinus = Math.random() < 0.5;

  if (useMinus) {
    const [big, small] = a >= b ? [a, b] : [b, a]; // never a negative answer
    return { text: `${big} - ${small}`, answer: big - small };
  }
  return { text: `${a} + ${b}`, answer: a + b };
};

// State for the security question. Call once per form, pass the result to <SecurityQuestion />
export const useSecurityQuestion = () => {
  const [question, setQuestion] = useState(generateQuestion);
  const [answer, setAnswer] = useState('');

  const isCorrect =
    answer.trim() !== '' && Number(answer.trim()) === question.answer;

  // New question + clear the answer
  const reset = useCallback(() => {
    setQuestion(generateQuestion());
    setAnswer('');
  }, []);

  return { question, answer, setAnswer, isCorrect, reset };
};

const SecurityQuestion = ({ captcha, fieldClass = '' }) => {
  const { question, answer, setAnswer, isCorrect, reset } = captcha;
  const [touched, setTouched] = useState(false);
  const isEmpty = answer.trim() === '';

  // a new question starts clean (no error shown)
  useEffect(() => setTouched(false), [question]);

  const showError = !isCorrect && (!isEmpty || touched);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="securityAnswer" className="text-white">
          Security question: what is {question.text}? *
        </Label>
        <button
          type="button"
          onClick={reset}
          className="text-slate-400 hover:text-white transition-colors"
          aria-label="Get a new security question"
          title="New question"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <Input
        id="securityAnswer"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={answer}
        onChange={(e) => setAnswer(e.target.value.replace(/[^\d]/g, ''))}
        onBlur={() => setTouched(true)}
        placeholder="Enter your answer"
        required
        aria-invalid={showError ? true : undefined}
        aria-describedby={showError ? 'securityAnswer-error' : undefined}
        className={`${fieldClass} ${
          showError ? 'border-red-500 focus:border-red-500 focus-visible:border-red-500' : ''
        }`}
      />
      {showError && (
        <p id="securityAnswer-error" role="alert" className="text-red-400 text-xs mt-1">{isEmpty ? 'Please answer the security question.' : 'Incorrect answer, please try again.'}</p>
      )}
    </div>
  );
};

export default SecurityQuestion;